import { makeStyles, tokens } from '@fluentui/react-components';

export const useManagerDashboardStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },

    // ── Role tabs ──
    roleTabs: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '0',
    },
    roleTabsLabel: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '14px',
        lineHeight: '20px',
        color: tokens.colorNeutralForeground3,
        marginRight: '4px',
    },
    roleTab: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 16px',
        borderRadius: '20px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground2,
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '14px',
        lineHeight: '20px',
        cursor: 'pointer',
        transitionProperty: 'all',
        transitionDuration: '0.15s',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    roleTabActive: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 16px',
        borderRadius: '20px',
        border: '1px solid #0078D4',
        backgroundColor: '#E8F4FD',
        color: '#0078D4',
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '14px',
        lineHeight: '20px',
        cursor: 'pointer',
    },

    // ── Hero wrapper ──
    heroWrapper: {
        borderRadius: '16px',
        overflow: 'visible',
        position: 'relative',
    },

    // ── Gradient top half ──
    heroGradient: {
        backgroundImage: 'linear-gradient(90.55deg, #0078D3 0.2%, #8DC7E8 99.27%)',
        padding: '40px 40px 80px 40px',
        minHeight: '186px',
        boxSizing: 'border-box',
        color: '#FFFFFF',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px 16px 0 0',
    },
    heroContent: {
        position: 'relative',
        zIndex: 2,
    },
    heroTitle: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightBold,
        fontSize: '32px',
        lineHeight: '40px',
        color: '#FFFFFF',
        margin: '0 0 8px 0',
    },
    heroSubtitle: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '16px',
        lineHeight: '24px',
        color: 'rgba(255, 255, 255, 0.9)',
        margin: '0',
    },
    heroDecoration: {
        position: 'absolute',
        right: '24px',
        top: '10px',
        width: '280px',
        height: '220px',
        zIndex: 1,
        pointerEvents: 'none',
        '& > img': {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        },
        '@media (max-width: 800px)': {
            display: 'none',
        },
    },

    // ── White bottom half ──
    heroBottom: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: tokens.colorNeutralBackground1,
        padding: '0 40px 24px 40px',
        borderRadius: '0 0 16px 16px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderTop: 'none',
    },
    heroBottomTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: '24px',
        flexWrap: 'wrap',
    },

    // ── Summary status cards ──
    summaryCards: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '-40px',
        position: 'relative',
        zIndex: 2,
        flex: '1 1 0',
    },
    heroSearchArea: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-end',
        gap: '8px',
        flex: '0 0 520px',
        width: '520px',
        maxWidth: '520px',
        minWidth: '520px',
        position: 'relative',
    },
    managerSelectionRow: {
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        minHeight: '24px',
        maxHeight: '52px',
        minWidth: 0,
        overflow: 'hidden',
    },
    selectedManagersList: {
        display: 'flex',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        gap: '6px',
        rowGap: '6px',
        flexWrap: 'wrap',
        width: '100%',
        maxHeight: '52px',
        minWidth: 0,
        overflow: 'hidden',
    },
    selectedManagerChip: {
        display: 'inline-flex',
        alignItems: 'center',
        flex: '0 1 auto',
        gap: '6px',
        borderRadius: '6px',
        backgroundColor: tokens.colorNeutralBackground3,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        padding: '2px 8px',
        maxWidth: '140px',
        minWidth: 0,
    },
    selectedManagerChipLabel: {
        fontFamily: tokens.fontFamilyBase,
        fontSize: '11px',
        lineHeight: '14px',
        color: tokens.colorNeutralForeground2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100px',
    },
    selectedManagerChipRemove: {
        border: 'none',
        background: 'transparent',
        color: tokens.colorNeutralForeground3,
        cursor: 'pointer',
        fontSize: '14px',
        lineHeight: '14px',
        padding: '0',
    },
    clearSelectedManagersButton: {
        border: '1px solid #C8C6C4',
        borderRadius: '7px',
        background: '#FFFFFF',
        color: '#323130',
        cursor: 'pointer',
        fontFamily: tokens.fontFamilyBase,
        fontSize: '12px',
        lineHeight: '18px',
        fontWeight: tokens.fontWeightSemibold,
        padding: '4px 12px',
        minHeight: '34px',
        minWidth: '68px',
        whiteSpace: 'nowrap',
        '&:hover:not(:disabled)': {
            background: '#F8F8F8',
        },
        '&:disabled': {
            background: '#F3F2F1',
            border: '1px solid #D1D1D1',
            color: '#A19F9D',
            cursor: 'not-allowed',
            opacity: 1,
        },
    },
    managerSearchRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        minWidth: 0,
    },
    summaryCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '16px',
        flex: '0 1 200px',
        minWidth: '140px',
        height: '124px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)',
        border: 'none',
    },
    summaryCardLabel: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
        lineHeight: tokens.lineHeightBase400,
        letterSpacing: '0',
        color: tokens.colorNeutralForeground1,
        margin: '0',
        display: 'block',
    },
    summaryCardValueRow: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '6px',
    },
    summaryCardCount: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeHero800,
        lineHeight: tokens.lineHeightHero800,
        letterSpacing: '0px',
        color: '#0F6CBD',
    },
    summaryCardCountAttention: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeHero800,
        lineHeight: tokens.lineHeightHero800,
        letterSpacing: '0px',
        color: '#D83B01',
    },
    summaryCardUnit: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '14px',
        lineHeight: '20px',
        color: tokens.colorNeutralForeground3,
        marginLeft: '4px',
    },

    // ── New hires section ──
    newHiresSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '16px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        padding: '24px',
    },
    newHiresSectionHeader: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
    },
    wobSearchBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '16px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        padding: '16px 24px',
    },
    managerLandingSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '16px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        padding: '24px',
    },
    managerLandingCopy: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: '0',
    },
    managerLandingSubtitle: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '14px',
        lineHeight: '20px',
        color: tokens.colorNeutralForeground3,
        margin: '0',
    },
    wobWelcomeState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        minHeight: '360px',
        padding: '56px 24px 72px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '16px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    wobWelcomeIllustration: {
        width: '160px',
        height: '160px',
        objectFit: 'contain',
        display: 'block',
    },
    wobWelcomeText: {
        maxWidth: '760px',
        margin: '0',
        textAlign: 'center',
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '14px',
        lineHeight: '22px',
        color: tokens.colorNeutralForeground2,
    },
    managerGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '16px',
    },
    managerCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        borderRadius: '12px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    managerCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '12px',
    },
    managerCardFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    managerName: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '16px',
        lineHeight: '22px',
        color: tokens.colorNeutralForeground1,
        margin: '0',
    },
    managerEmail: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '13px',
        lineHeight: '18px',
        color: tokens.colorNeutralForeground3,
        margin: '4px 0 0 0',
    },
    managerCardMeta: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '13px',
        lineHeight: '18px',
        color: tokens.colorNeutralForeground3,
    },
    newHiresTitle: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '24px',
        lineHeight: '32px',
        color: tokens.colorNeutralForeground1,
        margin: '0',
    },
    searchInput: {
        width: '100%',
        maxWidth: '600px',
        backgroundColor: tokens.colorNeutralBackground1,
    },
    managerSearchInput: {
        flex: '1 1 auto',
        width: '100%',
        maxWidth: 'none',
        minWidth: 0,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    wobSearchDropdown: {
        position: 'absolute',
        top: 'calc(100% + 8px)',
        left: 0,
        right: 0,
        zIndex: 10,
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: '12px',
        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.16)',
        overflow: 'hidden',
    },
    wobSearchDropdownItem: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        textAlign: 'left',
        padding: '10px 16px',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        fontFamily: tokens.fontFamilyBase,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    wobSearchDropdownEmpty: {
        padding: '12px 16px',
        fontFamily: tokens.fontFamilyBase,
        fontSize: '13px',
        lineHeight: '18px',
        color: tokens.colorNeutralForeground3,
    },

    // ── Table ──
    tableContainer: {
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px',
    },
    tableHeader: {
        textAlign: 'left',
        padding: '8px 16px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        color: tokens.colorNeutralForeground3,
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '12px',
        lineHeight: '16px',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
    },
    sortButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: 'none',
        border: 'none',
        padding: '0',
        margin: '0',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        whiteSpace: 'nowrap',
        '&:focus-visible': {
            outline: `2px solid ${tokens.colorBrandStroke1}`,
            outlineOffset: '2px',
            borderRadius: tokens.borderRadiusSmall,
        },
    },
    tableRow: {
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    tableCell: {
        padding: '8px 16px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '13px',
        lineHeight: '18px',
        verticalAlign: 'middle',
        color: tokens.colorNeutralForeground1,
    },
    candidateName: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '13px',
        lineHeight: '18px',
        color: tokens.colorNeutralForeground1,
    },

    // ── Progress column ──
    progressCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: '200px',
    },
    progressTopRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    progressText: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '12px',
        lineHeight: '16px',
        color: tokens.colorNeutralForeground2,
        whiteSpace: 'nowrap',
    },
    progressBarContainer: {
        width: '100%',
        height: '4px',
        backgroundColor: tokens.colorNeutralBackground5,
        borderRadius: '2px',
        overflow: 'hidden',
    },
    progressBarFill: {
        height: '100%',
        borderRadius: '2px',
        transitionProperty: 'width',
        transitionDuration: '0.3s',
        transitionTimingFunction: 'ease',
    },
    progressBarComplete: {
        background: 'linear-gradient(90.62deg, #D4EC8E 0.23%, #49C5B1 99.21%)',
    },
    progressBarInProgress: {
        background: 'linear-gradient(90.55deg, #0078D3 0.2%, #4191BF 99.27%)',
    },
    progressBarNeedsAttention: {
        backgroundColor: '#D83B01',
    },
    progressBarNotStarted: {
        backgroundColor: tokens.colorNeutralForeground4,
    },

    // ── Empty state ──
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        padding: '40px 0',
    },
    emptyStateText: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightRegular,
        fontSize: '14px',
        lineHeight: '20px',
        color: tokens.colorNeutralForeground2,
        margin: '0',
    },

    // ── Pagination ──
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        paddingTop: '16px',
        paddingBottom: '8px',
    },
    pageButton: {
        minWidth: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: 'transparent',
        color: tokens.colorNeutralForeground2,
        fontSize: '14px',
        fontFamily: tokens.fontFamilyBase,
        cursor: 'pointer',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    pageButtonActive: {
        minWidth: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#0F6CBD',
        color: '#FFFFFF',
        fontSize: '14px',
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        cursor: 'pointer',
    },
    pageNavButton: {
        minWidth: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: 'transparent',
        color: tokens.colorNeutralForeground3,
        fontSize: '14px',
        fontFamily: tokens.fontFamilyBase,
        cursor: 'pointer',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorNeutralForeground1,
        },
        ':disabled': {
            color: tokens.colorNeutralForegroundDisabled,
            cursor: 'not-allowed',
        },
    },
});
